<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useQuery } from '@tanstack/vue-query'
import { Plus } from 'lucide-vue-next'
import { CkAlert, CkCard, CkProgress } from '@ck/ui'
import { accountsApi, workOrdersApi } from '@ck/api-client'
import { identity } from '@ck/auth'
import BottomNav from '../components/BottomNav.vue'
import logoUrl from '../../design-system/logo-ck.png'

const router = useRouter()
const displayName = ref(identity().name ?? 'Mecânico')
const today = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short', weekday: 'short' }).format(new Date())

const workOrderQuery = useQuery({ queryKey: ['my-work-orders'], queryFn: () => workOrdersApi.list() })
const vehicles = computed(() => workOrderQuery.data.value?.items ?? [])
// A API já escopa /work-orders para as OS do mecânico autenticado: "veículos na oficina" é
// a contagem dessa lista (sem endpoint agregado — /dashboard/summary é Manager-only).
const vehiclesInShop = computed(() => vehicles.value.length)
const rejectedItems = computed(() => vehicles.value.reduce((sum, v) => sum + v.rejectedItemCount, 0))

onMounted(async () => {
  if (identity().name) return
  try {
    const me = await accountsApi.me()
    if (me.name) displayName.value = me.name
  } catch {
    // mantém o nome padrão quando /accounts/me não está disponível (ex.: authRequired=false)
  }
})
</script>

<template>
  <div class="app">
    <header>
      <div><span class="ck-serif-italic">Bom dia,</span><h1>{{ displayName }}</h1><div class="ck-eyebrow">{{ today }}</div></div>
      <img :src="logoUrl" alt="CK Car Repair Service">
    </header>

    <div class="stats">
      <CkCard tone="ink" class="stat"><strong>{{ vehiclesInShop }}</strong><span>veículos na oficina</span></CkCard>
      <CkCard class="stat stat--fail"><strong>{{ rejectedItems }}</strong><span>itens reprovados</span></CkCard>
    </div>

    <div class="section-head"><span class="ck-eyebrow">Ordens em andamento</span><button type="button" class="new" @click="router.push({ name: 'nova-os' })"><Plus :size="16"/>Nova OS</button></div>

    <CkAlert v-if="workOrderQuery.isError.value" tone="error">Não foi possível carregar suas ordens de serviço. Tente novamente em instantes.</CkAlert>
    <p v-else-if="workOrderQuery.isPending.value" class="status-message">Carregando…</p>
    <p v-else-if="vehicles.length === 0" class="status-message">Nenhuma ordem de serviço atribuída a você no momento.</p>
    <template v-else>
      <button
        v-for="workOrder in vehicles"
        :key="workOrder.id"
        type="button"
        class="card os"
        @click="router.push({ name: 'veiculo', params: { id: workOrder.id } })"
      >
        <div class="os-head">
          <div><strong>{{ workOrder.vehicle.brand }} {{ workOrder.vehicle.model }}</strong><span class="ck-code year">{{ workOrder.vehicle.year }}</span></div>
          <span class="ck-code number">{{ workOrder.number }}</span>
        </div>
        <p class="stage">{{ workOrder.currentStage ?? 'Sem etapa ativa' }} <span v-if="workOrder.currentStageSequence">· {{ workOrder.currentStageSequence }}/{{ workOrder.stageCount }}</span></p>
        <CkProgress :value="workOrder.progress"/>
        <div class="os-foot">
          <span v-if="workOrder.rejectedItemCount > 0" class="flag">{{ workOrder.rejectedItemCount }} reprovado(s)</span>
          <span v-else class="spacer"/>
          <strong class="pct">{{ workOrder.progress }}%</strong>
        </div>
      </button>
    </template>
    <BottomNav/>
  </div>
</template>

<style scoped>
.app{max-width:620px;min-height:100vh;margin:auto;padding:calc(20px + env(safe-area-inset-top)) 18px calc(94px + env(safe-area-inset-bottom));background:var(--ck-cream-100)}
header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:16px}
header h1{margin:0;font-size:28px}
header img{width:42px;height:64px;object-fit:contain}
.stats{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:18px}
.stat{padding:14px 16px}
.stat strong{display:block;font-family:var(--font-display);font-size:28px;font-weight:800;color:var(--ck-amber-400);line-height:1}
.stat--fail strong{color:var(--ck-fail-500)}
.stat span{font-size:12px;color:var(--ck-text-on-dark-muted)}
.stat--fail span{color:var(--ck-text-muted)}
.section-head{display:flex;justify-content:space-between;align-items:center;margin:4px 0 10px}
.new{display:inline-flex;align-items:center;gap:5px;border:0;background:transparent;color:var(--ck-orange-500);font-weight:700;font-size:13px;cursor:pointer}
.status-message{padding:20px 0;color:var(--ck-text-muted);font-size:13px}
.card{padding:16px;margin-bottom:12px;border:1px solid var(--ck-cream-300);border-radius:var(--radius-lg);background:var(--ck-cream-50);box-shadow:var(--shadow-sm)}
.os{display:block;width:100%;text-align:left;cursor:pointer;font:inherit;color:inherit;transition:border-color 200ms ease-out,box-shadow 200ms ease-out}
.os:hover,.os:focus-visible{border-color:var(--ck-orange-500)}
.os-head{display:flex;justify-content:space-between;align-items:baseline;gap:8px;margin-bottom:4px}
.os-head strong{font-size:15px}
.year{color:var(--ck-text-faint);margin-left:6px}
.number{color:var(--ck-text-muted);font-size:11px}
.stage{margin:0 0 8px;color:var(--ck-maroon-700);font-size:12px}
.os-foot{display:flex;justify-content:space-between;align-items:center;margin-top:8px}
.flag{background:var(--ck-fail-bg);color:var(--ck-fail-text);font-weight:700;font-size:11px;padding:3px 9px;border-radius:var(--radius-full)}
.spacer{flex:1}
.pct{color:var(--ck-orange-500);font-family:var(--font-mono);font-size:12px}
</style>
